import type CMSConnector from "../connector";
import type { CMSRestError } from "../rest/errors";
import type { CountryTranslations } from "../rest/types";
import Translator from "./translator";
import type {
  TranslationData,
  GeoInfo,
  LocaleData,
  Locale,
  Messages,
} from "./types";

type LocaleMap = Record<string, Locale>;
type TFunction = (msgId: string, substitutes?: Record<string, any>) => string;
type TNFunction = (
  msgId: string,
  n: number,
  substitutes?: Record<string, any>
) => string;

export default class I18n {
  cms: CMSConnector;

  defaultLocale!: Locale;
  translations!: LocaleData[];
  availableLocales!: LocaleMap;
  availableLanguages!: LocaleMap;
  userGeo?: GeoInfo;
  _userPreferredLocale: Locale | null = null;
  detectedLocale!: Locale;

  translator!: Translator;
  public countries: CountryTranslations = {};

  t!: TFunction;
  tn!: TNFunction;

  constructor(cms: CMSConnector) {
    this.cms = cms;
  }

  private get localStorageKey(): string {
    const c = this.cms.campaign;
    return `${c.app}:${c.id || c.app_id}:locale`;
  }

  private async getTranslations(): Promise<TranslationData> {
    // Load already loaded from campaign/html tags
    if (this.cms.campaign.i18n) {
      return this.cms.campaign.i18n;
    }
    // TODO: Should this be kept for something?
    if (this.cms.campaign.id) {
      try {
        return await this.cms.api.i18n.getCampaignTranslations(
          this.cms.campaign.id
        );
      } catch (error) {
        if ((error as CMSRestError).status === 404) {
          console.warn(error);
        } else {
          throw error;
        }
      }
    }
    if (this.cms.campaign.app) {
      return await this.cms.api.i18n.getAppTranslations(
        this.cms.campaign.app,
        true
      );
    } else {
      throw new Error("App or Campaign ID must be set to load translations");
    }
  }

  private async loadTranslations(): Promise<void> {
    const data = await this.getTranslations();
    this.translations = data.translations;
    const locales: LocaleMap = {};
    const languages: LocaleMap = {};
    for (const t of data.translations) {
      locales[t.locale.code] = t.locale;
      languages[t.locale.code.split("-")[0]] = t.locale;
    }
    this.availableLocales = locales;
    this.availableLanguages = languages;
    this.defaultLocale =
      this.availableLocales[data.defaultLocale] || data.translations[0].locale;
  }

  public async loadUserGeo(): Promise<void> {
    const data = await this.cms.api.i18n.getUserGeo();
    this.userGeo = {
      countryCode: data.country_code,
      languageCode: data.language_code,
    };
  }

  public async loadCountries(): Promise<void> {
    this.countries = await this.cms.api.i18n.getCountries();
  }

  public async load(): Promise<void> {
    await this.loadTranslations();
    await this.loadUserGeo();
    this.detectedLocale = this.detectLocale();
    await this.loadCountries();
    this.makeTranslator();
  }

  private makeTranslator(): void {
    const locale = this.currentLocale;
    let messages = this.translations.find(
      (d) => d.locale.code === locale.code
    )?.messages;
    if (!messages) {
      console.warn("Translations not loaded for locale:", locale);
      messages = {};
    }
    this.translator = new Translator(messages, locale.pluralForm);
    this.t = this.translator.t.bind(this.translator);
    this.tn = this.translator.tn.bind(this.translator);
  }

  private detectLocale(): Locale {
    const preferredCode = localStorage.getItem(this.localStorageKey);
    if (preferredCode) {
      if (preferredCode in this.availableLocales) {
        return this.availableLocales[preferredCode];
      }
    }
    const navLangs = navigator.languages;
    for (const locale of navLangs) {
      if (locale in this.availableLocales) {
        return this.availableLocales[locale];
      }
    }
    for (const locale of navLangs) {
      const lang = locale.split("-")[0];
      if (lang in this.availableLanguages) {
        return this.availableLanguages[lang];
      }
    }
    if (this.userGeo) {
      const geoLang = this.userGeo.languageCode;
      const geoCC = this.userGeo.countryCode;
      const geoLocale = geoLang + "-" + geoCC;
      if (geoLocale in this.availableLocales) {
        return this.availableLocales[geoLocale];
      }
      if (geoLang && geoLang in this.availableLanguages) {
        return this.availableLanguages[geoLang];
      }
    }
    return this.defaultLocale;
  }

  public get currentLocaleCountries(): Record<string, string> {
    return (
      this.countries[this.currentLocale?.code] ||
      this.countries[this.defaultLocale.code] ||
      this.countries["en"] ||
      {}
    );
  }

  /**
   * Translate from external messages. Messages are object
   * in form of {"locale-code": {"messageId": "message"}}
   * @param messages
   * @param msgId
   * @param substitutes
   */
  public tx(
    messages: Record<string, Messages>,
    msgId: string,
    substitutes?: Record<string, any>
  ): string {
    const messagesLocale = messages[this.currentLocale.code];
    if (!messagesLocale) {
      console.warn("Object missing current locale");
    }
    return this.translator.tx(messagesLocale || {}, msgId, substitutes);
  }

  get currentLocale(): Locale {
    return this._userPreferredLocale || this.detectedLocale;
  }

  get userCountryCode(): string | null {
    return this.userGeo?.countryCode || null;
  }

  set userPreferredLocale(localeCode: string | null) {
    this._userPreferredLocale = localeCode
      ? this.availableLocales[localeCode] || null
      : null;
    if (localeCode === null) {
      localStorage.removeItem(this.localStorageKey);
    } else {
      localStorage.setItem(this.localStorageKey, localeCode);
    }
    this.makeTranslator();
  }
}
